/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { isBoostPortal, type CatalogPublication } from '@/features/publications-catalog/types';
import {
  FF_APP_CHAVES_NA_MAO, FF_APP_FACEBOOK, FF_APP_OLX, FF_APP_PORTAL_FEEDS, FF_APP_PORTALS_INTEGRATION,
} from '@/shared/utils/feature-flag';
import { actionPortalForIndex, buildRows, leadAdFields } from './rows';
import type { PortalPublication, PublicationRow, RowBoost } from './types';

/** Re-export for consumers that only need the portal action helper. */
export { actionPortalForIndex };

const PORTAL_KEYS = new Set(['olx', 'zap', 'vivareal', 'chavesnamao']);

/**
 * Normalizes a value into an array of identifiers.
 */
function toIdArray(arr: string[] | undefined, single: string | undefined): string[] {
  return arr || (single ? [single] : []);
}

/**
 * Read live post/listing id arrays from a portal publication.
 */
function liveIdLists(live: PortalPublication | undefined): { posts: string[]; listings: string[] } {
  if (!live) return { posts: [], listings: [] };
  return {
    posts: toIdArray(live.post_ids, live.post_id),
    listings: toIdArray(live.listing_ids, live.listing_id),
  };
}

/**
 * Status for one catalog entry, preferring live ids and treating orphans as published.
 */
export function resolveCatalogEntryStatus(
  entry: CatalogPublication,
  live: PortalPublication | undefined,
): string {
  const pubId = entry.publication_id;
  if (pubId.startsWith('pending:')) {
    return live?.status ?? entry.status ?? 'queued';
  }
  const { posts, listings } = liveIdLists(live);
  if (posts.includes(pubId) || listings.includes(pubId) || live?.ad_id === pubId) {
    return live?.status ?? entry.status ?? 'published';
  }
  return 'published';
}

const CATALOG_ROW_KEY_MAP: Record<string, (id: string) => string> = {
  facebook_marketplace: (id) => `facebook-listing-${id}`,
  facebook_lead_ad: (id) => `facebook-ad-${id}`,
  facebook_feed: (id) => `facebook-post-${id}`,
  instagram: (id) => `instagram-post-${id}`,
};

/**
 * Stable row key for a catalog index portal + publication id.
 */
function catalogRowKey(indexPortal: string, publicationId: string): string {
  if (publicationId.startsWith('pending:')) return `${actionPortalForIndex(indexPortal)}-pending`;
  const mapper = CATALOG_ROW_KEY_MAP[indexPortal];
  return mapper ? mapper(publicationId) : `${indexPortal}-${publicationId}`;
}

/**
 * Map catalog publication_id onto PublicationRow post/listing/ad fields.
 */
function idsForCatalogEntry(
  indexPortal: string,
  publicationId: string,
): Pick<PublicationRow, 'post_id' | 'listing_id' | 'ad_id'> {
  if (publicationId.startsWith('pending:')) return {};
  if (indexPortal === 'facebook_marketplace') return { listing_id: publicationId };
  if (indexPortal === 'facebook_lead_ad') return { ad_id: publicationId };
  // A portal ad's id is its listing id (OLX ``list_id``), as on the live
  // publish-status row: as a post_id it never matched that row (shown twice)
  // and sent "Editar" to the Facebook content endpoint.
  if (PORTAL_KEYS.has(indexPortal)) return { listing_id: publicationId };
  return { post_id: publicationId };
}

/** True when a Meta action (facebook/instagram) is disabled by its flag. */
function isMetaActionDisabled(action: string): boolean {
  const isMeta = action === 'facebook' || action === 'instagram';
  return !FF_APP_FACEBOOK && isMeta;
}

/** True when a feed action (olx or another feed portal) is disabled by its flag. */
function isFeedActionDisabled(action: string): boolean {
  if (action === 'olx') return !FF_APP_OLX;
  if (action === 'chavesnamao') return !(FF_APP_CHAVES_NA_MAO || FF_APP_PORTAL_FEEDS);
  return PORTAL_KEYS.has(action) && !FF_APP_PORTAL_FEEDS;
}

/**
 * Whether a catalog index portal is enabled by feature flags.
 */
function isCatalogPortalEnabled(indexPortal: string): boolean {
  const action = actionPortalForIndex(indexPortal);
  if (isMetaActionDisabled(action)) return false;
  if (isFeedActionDisabled(action)) return false;
  return !PORTAL_KEYS.has(action) || FF_APP_PORTALS_INTEGRATION;
}

/**
 * Build one property-tab row from a catalog index entry.
 */
const LEAD_AD_CATALOG_PORTALS = new Set(['facebook_feed', 'instagram']);

/**
 * The Lead Ad state of a catalog row. The outcome stored on the post's own
 * entry wins: it belongs to that post and survives later publishes. The live
 * publish status only fills in for a post that has no stored outcome yet
 * (still being created, or not indexed yet).
 */
function leadAdFieldsFor(
  entry: CatalogPublication,
  live: PortalPublication | undefined,
): Pick<PublicationRow, 'lead_ad_status' | 'lead_ad_error' | 'lead_ad_id' | 'lead_ad_paused'> {
  if (entry.lead_ad_status) {
    return {
      lead_ad_status: entry.lead_ad_status,
      ...(entry.lead_ad_error ? { lead_ad_error: entry.lead_ad_error } : {}),
      ...(entry.lead_ad_id ? { lead_ad_id: entry.lead_ad_id } : {}),
      ...(entry.lead_ad_paused_at ? { lead_ad_paused: true } : {}),
    };
  }
  return liveLeadAdFields(entry, live);
}

/**
 * The live Lead Ad outcome for a catalog row -- only when that outcome belongs
 * to the publish this row is from (the row is the still-pending one, or its
 * post is among the live publish's posts), never an older post's row.
 */
function liveLeadAdFields(
  entry: CatalogPublication,
  live: PortalPublication | undefined,
): Pick<PublicationRow, 'lead_ad_status' | 'lead_ad_error'> {
  if (!live || !LEAD_AD_CATALOG_PORTALS.has(entry.portal)) return {};
  const pubId = entry.publication_id;
  const ownsOutcome = pubId.startsWith('pending:') || liveIdLists(live).posts.includes(pubId);
  return ownsOutcome ? leadAdFields(live) : {};
}

/**
 * Maps a single catalog entry into a display-ready publication row.
 *
 * @param entry - The catalog publication entry.
 * @param live - The live portal publication, if any.
 * @param getPortalLabel - Portal name resolver.
 * @returns The publication row, or null when the entry should be hidden.
 */
export function catalogEntryToRow(
  entry: CatalogPublication,
  live: PortalPublication | undefined,
  getPortalLabel: (p: string) => string,
): PublicationRow | null {
  if (isBoostPortal(entry.portal) || !isCatalogPortalEnabled(entry.portal)) return null;
  const action = actionPortalForIndex(entry.portal);
  const labelKey = entry.portal.startsWith('facebook_') ? entry.portal : action;
  return {
    key: catalogRowKey(entry.portal, entry.publication_id),
    portal: action,
    label: getPortalLabel(labelKey),
    status: resolveCatalogEntryStatus(entry, live),
    published_at: entry.published_at,
    permalink: entry.permalink,
    ...idsForCatalogEntry(entry.portal, entry.publication_id),
    ...leadAdFieldsFor(entry, live),
  };
}

/**
 * Dedup key so catalog rows and live-status rows are not shown twice.
 */
function coverageKey(row: PublicationRow): string {
  if (row.post_id) return `${row.portal}:post:${row.post_id}`;
  if (row.listing_id) return `${row.portal}:listing:${row.listing_id}`;
  if (row.ad_id) return `${row.portal}:ad:${row.ad_id}`;
  return `${row.portal}:pending`;
}

/**
 * Merge agency catalog entries for a property with live publish-status rows.
 * Catalog is the source of history; live status fills feed-only portals and gaps.
 */
/** Boosts of this property, grouped by the post they run on. */
function boostsByPost(entries: CatalogPublication[]): Map<string, RowBoost[]> {
  const byPost = new Map<string, RowBoost[]>();
  for (const entry of entries) {
    if (!isBoostPortal(entry.portal) || !entry.boosted_post_id) continue;
    const boosts = byPost.get(entry.boosted_post_id) ?? [];
    boosts.push({
      ad_id: entry.publication_id,
      daily_budget_cents: entry.daily_budget_cents,
      duration_days: entry.duration_days,
      ...(entry.paused_at ? { paused_at: entry.paused_at } : {}),
    });
    byPost.set(entry.boosted_post_id, boosts);
  }
  return byPost;
}

/** Whether a row is a Facebook feed post (not a Marketplace listing or another portal). */
function isFacebookPostRow(row: PublicationRow): boolean {
  return row.portal === 'facebook' && Boolean(row.post_id);
}

/**
 * The Lead Ad id of each publish, keyed by its publish time. A publish writes
 * its feed post and its Lead Ad to the index with the same `published_at`,
 * which is how an ad is matched back to the post it was created with.
 */
function leadAdIdsByPublish(entries: CatalogPublication[]): Map<string, string> {
  const byPublish = new Map<string, string>();
  for (const entry of entries) {
    if (entry.portal === 'facebook_lead_ad' && !entry.publication_id.startsWith('pending:')) {
      byPublish.set(entry.published_at, entry.publication_id);
    }
  }
  return byPublish;
}

/**
 * Joins catalog entries with live portal publications into a flat, display-ready row array.
 *
 * @param publications - Live portal publications keyed by portal.
 * @param catalog - The full publication catalog.
 * @param propertyId - The property to collect rows for.
 * @param getPortalLabel - Portal name resolver.
 * @returns Merged publication rows, including boost rows where applicable.
 */
export function mergePropertyPublicationRows(
  publications: Record<string, PortalPublication>,
  catalog: CatalogPublication[],
  propertyId: string,
  getPortalLabel: (p: string) => string,
): PublicationRow[] {
  const forProperty = catalog.filter((e) => e.property_id === propertyId);
  const leadAdIds = leadAdIdsByPublish(forProperty);
  const fromCatalog: PublicationRow[] = [];
  for (const entry of forProperty) {
    // A Lead Ad is shown on its post's row, never as a row of its own.
    if (entry.portal === 'facebook_lead_ad') continue;
    const live = publications[actionPortalForIndex(entry.portal)];
    const row = catalogEntryToRow(entry, live, getPortalLabel);
    if (row) fromCatalog.push(row);
  }
  const covered = new Set(fromCatalog.map(coverageKey));
  const extras = buildRows(publications, getPortalLabel).filter(
    (row) => !covered.has(coverageKey(row)),
  );
  const adState = {
    boosts: boostsByPost(forProperty),
    leadAdIds,
    pausedAds: new Set(forProperty.filter((e) => e.paused_at).map((e) => e.publication_id)),
  };
  return [...fromCatalog, ...extras].map((row) => withAdState(row, adState));
}

/** What the catalog knows about the ads running on a property's posts. */
interface AdState {
  boosts: Map<string, RowBoost[]>;
  leadAdIds: Map<string, string>;
  pausedAds: Set<string>;
}

/** The Lead Ad id of a row: its post's own record names it; older Facebook Lead Ads match by publish time. */
function leadAdIdOf(row: PublicationRow, leadAdIds: Map<string, string>): string | undefined {
  if (row.lead_ad_id) return row.lead_ad_id;
  return isFacebookPostRow(row) ? leadAdIds.get(row.published_at ?? '') : undefined;
}

/** A row with its boosts, Lead Ad id and paused state attached. */
function withAdState(row: PublicationRow, state: AdState): PublicationRow {
  const boosts = row.post_id ? state.boosts.get(row.post_id) : undefined;
  const leadAdId = leadAdIdOf(row, state.leadAdIds);
  const paused = Boolean(row.lead_ad_paused) || (leadAdId !== undefined && state.pausedAds.has(leadAdId));
  return {
    ...row,
    ...(boosts ? { boosts } : {}),
    ...(leadAdId ? { lead_ad_id: leadAdId } : {}),
    ...(paused ? { lead_ad_paused: true } : {}),
  };
}
